import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Star, Plus, Trash2, Share2, MessageSquare, ChevronDown, ChevronUp, Info, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StarRating = ({ rating, onRate, size = 'md', readOnly = false }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const sizes = {
    sm: 14,
    md: 20,
    lg: 28
  };
  const starSize = sizes[size] || sizes.md;
  
  return (
    <div 
      style={{ display: 'flex', gap: '2px' }}
      onMouseLeave={() => !readOnly && setHoverRating(0)}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          onClick={() => !readOnly && onRate && onRate(star)}
          onMouseEnter={() => !readOnly && setHoverRating(star)}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '2px',
            cursor: readOnly ? 'default' : 'pointer',
            transition: 'transform 0.1s ease'
          }}
          disabled={readOnly}
        >
          <Star
            size={starSize}
            fill={(hoverRating || rating) >= star ? '#fbbf24' : 'transparent'}
            stroke="#fbbf24"
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
};

const MyGearSection = ({ user, onShareGear }) => {
  const [myGear, setMyGear] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const fetchMyGear = async () => {
    try {
      const token = localStorage.getItem('beatseek_token');
      if (!token) return;
      
      const response = await fetch(`${BACKEND_URL}/api/gear/user/collection`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMyGear(data.gear || []);
      }
    } catch (err) {
      console.error('Failed to fetch gear:', err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchMyGear();
  }, []);
  
  const handleRemoveGear = async (gearId) => {
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/gear/user/collection/${gearId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setMyGear(prev => prev.filter(g => g.gear_id !== gearId));
      }
    } catch (err) {
      console.error('Failed to remove gear:', err);
    }
  };
  
  return (
    <Card style={{
      background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(30, 30, 50, 0.9) 100%)',
      border: '1px solid rgba(251, 191, 36, 0.3)',
      marginTop: '20px'
    }}>
      <CardHeader 
        style={{ 
          borderBottom: '1px solid rgba(251, 191, 36, 0.2)',
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <CardTitle style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: '#fff'
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            🎛️ My Gear ({myGear.length})
          </span>
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </CardTitle>
      </CardHeader>
      
      {expanded && (
        <CardContent style={{ padding: '20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: '#888', padding: '20px' }}>
              Loading your gear...
            </div>
          ) : myGear.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px' }}>
              <p style={{ color: '#888', marginBottom: '15px' }}>
                You haven&apos;t added any gear to your collection yet.
              </p>
              <p style={{ color: '#666', fontSize: '13px', marginBottom: '20px' }}>
                Rate gear in the Gear Reviews section and mark items you own!
              </p>
              <Button
                onClick={() => setShowAddForm(true)}
                style={{
                  background: 'rgba(251, 191, 36, 0.2)',
                  border: '1px solid #fbbf24',
                  color: '#fbbf24'
                }}
              >
                <Plus size={16} style={{ marginRight: '5px' }} />
                Add Custom Gear
              </Button>
            </div>
          ) : (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '15px',
                marginBottom: '20px'
              }}>
                {myGear.map((gear) => (
                  <div
                    key={gear.gear_id}
                    style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '10px',
                      padding: '15px',
                      border: '1px solid rgba(251, 191, 36, 0.2)'
                    }}
                  >
                    {gear.image_url && (
                      <img
                        src={gear.image_url}
                        alt={gear.gear_name}
                        style={{
                          width: '100%',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '6px',
                          marginBottom: '10px'
                        }}
                      />
                    )}
                    <div style={{ color: '#fff', fontWeight: '600', fontSize: '14px', marginBottom: '5px' }}>
                      {gear.gear_name}
                    </div>
                    <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px' }}>
                      {gear.gear_brand}
                    </div>
                    {gear.user_rating && (
                      <StarRating rating={gear.user_rating} readOnly size="sm" />
                    )}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                      {onShareGear && (
                        <button
                          onClick={() => onShareGear(gear)}
                          style={{
                            flex: 1,
                            padding: '6px',
                            background: 'rgba(88, 101, 242, 0.2)',
                            border: '1px solid #5865F2',
                            borderRadius: '4px',
                            color: '#5865F2',
                            cursor: 'pointer',
                            fontSize: '11px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px'
                          }}
                        >
                          <Share2 size={12} /> Share
                        </button>
                      )}
                      <button
                        onClick={() => handleRemoveGear(gear.gear_id)}
                        style={{
                          padding: '6px 10px',
                          background: 'rgba(239, 68, 68, 0.2)',
                          border: '1px solid rgba(239, 68, 68, 0.5)',
                          borderRadius: '4px',
                          color: '#ef4444',
                          cursor: 'pointer'
                        }}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <Button
                onClick={() => setShowAddForm(true)}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: '1px dashed rgba(251, 191, 36, 0.5)',
                  color: '#fbbf24'
                }}
              >
                <Plus size={16} style={{ marginRight: '5px' }} />
                Add Custom Gear
              </Button>
            </>
          )}
          
          {showAddForm && (
            <AddGearReviewForm 
              onClose={() => setShowAddForm(false)}
              onSuccess={() => {
                setShowAddForm(false);
                fetchMyGear();
              }}
            />
          )}
        </CardContent>
      )}
    </Card>
  );
};

const AddGearReviewForm = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    gear_name: '',
    gear_brand: '',
    gear_category: 'controller',
    rating: 0,
    review_text: '',
    image_url: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showImageHelp, setShowImageHelp] = useState(false);
  
  const categories = [
    { value: 'controller', label: 'DJ Controller' },
    { value: 'turntable', label: 'Turntable' },
    { value: 'mixer', label: 'Mixer' },
    { value: 'headphones', label: 'Headphones' },
    { value: 'speakers', label: 'Speakers/Monitors' },
    { value: 'software', label: 'Software' },
    { value: 'lighting', label: 'Lighting' },
    { value: 'other', label: 'Other' }
  ];
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.gear_name || !formData.gear_brand || !formData.rating || !formData.review_text) {
      setError('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/gear/review/custom`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        onSuccess();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to submit review');
      }
    } catch (err) {
      setError('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 99998,
      padding: '20px'
    }}
    onClick={onClose}
    >
      <div 
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          borderRadius: '16px',
          border: '2px solid #fbbf24',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '85vh',
          overflow: 'auto',
          padding: '25px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ color: '#fbbf24', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          🎛️ Add Gear Review
        </h2>
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ color: '#888', fontSize: '12px', display: 'block', marginBottom: '5px' }}>
              GEAR NAME *
            </label>
            <input
              type="text"
              value={formData.gear_name}
              onChange={(e) => setFormData(prev => ({ ...prev, gear_name: e.target.value }))}
              placeholder="e.g., DDJ-1000"
              style={{
                width: '100%',
                padding: '12px',
                background: '#2f3136',
                border: '1px solid #40444b',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ color: '#888', fontSize: '12px', display: 'block', marginBottom: '5px' }}>
              BRAND *
            </label>
            <input
              type="text"
              value={formData.gear_brand}
              onChange={(e) => setFormData(prev => ({ ...prev, gear_brand: e.target.value }))}
              placeholder="e.g., Pioneer DJ"
              style={{
                width: '100%',
                padding: '12px',
                background: '#2f3136',
                border: '1px solid #40444b',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ color: '#888', fontSize: '12px', display: 'block', marginBottom: '5px' }}>
              CATEGORY
            </label>
            <select
              value={formData.gear_category}
              onChange={(e) => setFormData(prev => ({ ...prev, gear_category: e.target.value }))}
              style={{
                width: '100%',
                padding: '12px',
                background: '#2f3136',
                border: '1px solid #40444b',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '14px'
              }}
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ color: '#888', fontSize: '12px', display: 'block', marginBottom: '8px' }}>
              YOUR RATING *
            </label>
            <StarRating 
              rating={formData.rating} 
              onRate={(r) => setFormData(prev => ({ ...prev, rating: r }))}
              size="lg"
            />
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ color: '#888', fontSize: '12px', display: 'block', marginBottom: '5px' }}>
              YOUR REVIEW *
            </label>
            <textarea
              value={formData.review_text}
              onChange={(e) => setFormData(prev => ({ ...prev, review_text: e.target.value }))}
              placeholder="Share your experience with this gear..."
              rows={4}
              style={{
                width: '100%',
                padding: '12px',
                background: '#2f3136',
                border: '1px solid #40444b',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '14px',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ color: '#888', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' }}>
              IMAGE URL (optional)
              <button
                type="button"
                onClick={() => setShowImageHelp(true)}
                style={{
                  background: 'rgba(251, 191, 36, 0.2)',
                  border: '1px solid rgba(251, 191, 36, 0.4)',
                  borderRadius: '50%',
                  width: '18px',
                  height: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  padding: 0
                }}
                title="How to add an image"
              >
                <Info size={12} style={{ color: '#fbbf24' }} />
              </button>
            </label>
            <input
              type="url"
              value={formData.image_url}
              onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
              placeholder="https://example.com/image.jpg"
              style={{
                width: '100%',
                padding: '12px',
                background: '#2f3136',
                border: '1px solid #40444b',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          {error && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              padding: '10px 15px',
              marginBottom: '15px',
              color: '#ef4444',
              fontSize: '13px'
            }}>
              {error}
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                background: 'transparent',
                border: '1px solid #666',
                color: '#888'
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              style={{
                flex: 2,
                background: '#fbbf24',
                border: 'none',
                color: '#000',
                fontWeight: '600'
              }}
            >
              {submitting ? 'Submitting...' : 'Submit Review'}
            </Button>
          </div>
        </form>
        
        {/* Image Help Modal */}
        {showImageHelp && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 999999,
              padding: '20px'
            }}
            onClick={() => setShowImageHelp(false)}
          >
            <div 
              style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                borderRadius: '16px',
                border: '2px solid #fbbf24',
                maxWidth: '450px',
                width: '100%',
                padding: '25px'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ color: '#fbbf24', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Info size={20} />
                  How to Add a Gear Image
                </h3>
                <button
                  onClick={() => setShowImageHelp(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#888',
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              
              <div style={{ color: '#ccc', fontSize: '14px', lineHeight: '1.6' }}>
                <p style={{ marginBottom: '15px' }}>
                  To add an image of your gear, you&apos;ll need to upload it to a free image hosting service first:
                </p>
                
                <div style={{ 
                  background: 'rgba(251, 191, 36, 0.1)', 
                  border: '1px solid rgba(251, 191, 36, 0.3)',
                  borderRadius: '8px',
                  padding: '15px',
                  marginBottom: '15px'
                }}>
                  <h4 style={{ color: '#fbbf24', margin: '0 0 10px 0', fontSize: '14px' }}>📸 Using Imgur (Recommended)</h4>
                  <ol style={{ margin: '0', paddingLeft: '20px', color: '#aaa', fontSize: '13px' }}>
                    <li style={{ marginBottom: '8px' }}>Go to <a href="https://imgur.com/upload" target="_blank" rel="noopener noreferrer" style={{ color: '#00ffff' }}>imgur.com/upload</a></li>
                    <li style={{ marginBottom: '8px' }}>Drag &amp; drop your image or click to browse</li>
                    <li style={{ marginBottom: '8px' }}>Wait for upload to complete</li>
                    <li style={{ marginBottom: '8px' }}>Right-click the image → &quot;Copy image address&quot;</li>
                    <li style={{ marginBottom: '0' }}>Paste the URL in the Image URL field</li>
                  </ol>
                </div>
                
                <p style={{ color: '#888', fontSize: '12px', margin: '0' }}>
                  <strong>Other options:</strong> imgbb.com, postimages.org, or any image hosting service that provides direct image links.
                </p>
              </div>
              
              <button
                onClick={() => setShowImageHelp(false)}
                style={{
                  marginTop: '20px',
                  width: '100%',
                  padding: '12px',
                  background: '#fbbf24',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#000',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Got it!
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export { StarRating, MyGearSection, AddGearReviewForm };
export default MyGearSection;
